// One family in the customer list: who, which services, stage and value; expands into the full detail.

import React from "react";
import { Avatar, Box, Collapse, Divider, IconButton, Paper, Stack, Tooltip, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import type { CrmCustomer, Identity } from "../../shared/crm";
import type { CrmActions, Planned } from "./actions";
import { CustomerDetail } from "./CustomerDetail";
import { STAGE_COLOR, ServiceBadges, StageChip, ageText, initials, money, monthDate } from "./ui";

export const CustomerCard: React.FC<{
  customer: CrmCustomer;
  open: boolean;
  onToggle: () => void;
  today: string;
  identities: Map<string, Identity>;
  actions: CrmActions;
  run: (planned: Planned, message: string) => void;
}> = ({ customer, open, onToggle, today, identities, actions, run }) => {
  const theme = useTheme();
  const color = STAGE_COLOR[customer.stage];
  const accent = color === "default" ? theme.palette.text.disabled : theme.palette[color].main;
  const predicted = customer.prediction.preschoolPence + customer.prediction.holidayPence;
  const kids = customer.children.map((c) => {
    const age = ageText(c.dateOfBirth, today);
    return `${c.name.split(" ")[0]}${age ? ` (${age.split(" ")[0]})` : ""}`;
  }).join(", ");

  return (
    <Paper variant="outlined" sx={{
      borderLeft: `4px solid ${accent}`,
      opacity: customer.stage === "alumni" ? 0.85 : 1,
      transition: "box-shadow 150ms",
      "&:hover": { boxShadow: theme.shadows[2] },
    }}>
      <Stack direction={{ xs: "column", md: "row" }} alignItems={{ md: "center" }} spacing={{ xs: 1, md: 2 }}
        sx={{ px: 2, py: 1.5, cursor: "pointer" }} onClick={onToggle}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          <Avatar sx={{ bgcolor: alpha(accent, 0.15), color: accent, fontWeight: 600, width: 40, height: 40 }}>{initials(customer.name)}</Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography fontWeight={600} noWrap>{customer.name}</Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {kids || "No children on record"}
              {customer.lastActivity ? ` · last ${monthDate(customer.lastActivity)}` : ""}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <ServiceBadges services={customer.services} />
          <StageChip customer={customer} />
          {customer.extraDocIds.length > 0 && (
            <Tooltip title="Linked records from more than one saved family; the next change here tidies them up">
              <Typography variant="caption" color="warning.main">•</Typography>
            </Tooltip>
          )}
        </Stack>

        <Stack direction="row" alignItems="center" spacing={2} justifyContent="flex-end" sx={{ minWidth: { md: 300 } }}>
          <Box sx={{ textAlign: "right", flex: { xs: 1, md: "none" } }}>
            <Typography variant="caption" color="text.secondary" component="div">Realised</Typography>
            <Typography sx={{ fontVariantNumeric: "tabular-nums" }}>{money(customer.realised.invoicedPence)}</Typography>
          </Box>
          <Box sx={{ textAlign: "right", minWidth: 80 }}>
            <Typography variant="caption" color="text.secondary" component="div">Predicted</Typography>
            <Typography sx={{ fontVariantNumeric: "tabular-nums" }} color={predicted ? "text.primary" : "text.disabled"}>{money(predicted)}</Typography>
          </Box>
          <Box sx={{ textAlign: "right", minWidth: 80 }}>
            <Typography variant="caption" color="text.secondary" component="div">Lifetime</Typography>
            <Typography fontWeight={700} sx={{ fontVariantNumeric: "tabular-nums" }}>{money(customer.ltvPence)}</Typography>
          </Box>
          <IconButton size="small" aria-label={open ? "Hide details" : "Show details"}
            sx={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 150ms" }}>
            <ExpandMoreIcon />
          </IconButton>
        </Stack>
      </Stack>
      <Collapse in={open} unmountOnExit>
        <Divider />
        <CustomerDetail customer={customer} today={today} identities={identities} actions={actions} run={run} />
      </Collapse>
    </Paper>
  );
};
